import { Component, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { ComponentMessageDispatcher } from '../../../core/service/component-message-dispather.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { leaseAgreementCommand } from '../../command/lease-agreement.command';
import { ContractService } from '../../service/base/contract';
import { ApproveContractCommandHandler } from '../../service/command/approve-contact-command-handler.service';
import { PrintContractCommandHandler } from '../../service/command/print-contract-command-handler.service';
import { PrintContractTerminationFormCommandHandler } from '../../service/command/print-contract-termination-form-command-handler';
import { ShowLeaseAgreementInvoicesCommandHandler } from '../../service/command/show-lease-agreement-invoices-command-handler';
import { TerminateContractCommandHandler } from '../../service/command/terminate-contract-command-handler.service';
import { WithdrawApprovalCommandHandler } from '../../service/command/withdraw-approval-command-handler.service';
import { WithdrawTerminationCommandHandler } from '../../service/command/withdraw-termination-command-handler.service';
import { ContractFormComponentBase } from '../base/contract-form.base';
import { ActivatedRoute } from '@angular/router';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { SubleaseRequest } from '../../model/sublease-request';
import { Command, standardCommand } from '../../../core/model/command';
import { VoidContractCommandHandler } from '../../service/command/void-contract-command-handler.service';
import { ESignLeaseAgreementContractCommandHandler } from '../../service/command/esign-lease-agreement-document-command-handler.service';
import { UserAccountService } from '../../../common/service/user-account.service';
import { ESignKeyRecieptCommandHandler } from '../../service/command/esign-key-reciept-document-command-handler.service';
import { CooperativeHousingContract } from '../../model/cooperative-housing-contract';
import { CooperativeHousingContractService } from '../../service/data/cooperative-housing-contract.service';
import { CooperativeHousingOwner } from '../../model/CooperativeHousingOwner';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { mailCommand } from '../../../message/model/const/command';
import { LocalDate } from '../../../core/model/local-date';
import { VacantRentalObject } from '../../model/vacant-rental-object';
import { Money } from '../../../core/model/money';
import { AutoCompleteSelectionItem } from '../../../core/component/autocomplete-remote-paged/autocomplete-remote-paged.component';
import { TenantService } from '../../service/data/tenant.service';
import { numberValue } from '../../../core/helper/number-value';
import { TransferCooperativeHousingCommandHandler } from '../../service/command/transfer-cooperative-housing-command-handler.service';
import { CooperativeHousingContractFormCommandHandlerResolver } from '../../service/command/cooperative-housing-contract-form-command-handler-resolver.service';
import { BrfMortgagePrescription } from '../../model/brf-mortgage-prescription';
import { CreditInstituteService } from '../../../mortgage/service/credit-institute.service';
import { LeaseAgreementLogService } from '../../service/data/lease-agreement-log.service';
import { LeaseAgreementCategory, LeaseAgreementStatus, RentalObjectCategory } from '../../model/const/enums';
import { LeaseAgreementImdConsumptionService } from '../../service/data/lease-agreement-imd-consumption.service';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { LanguageService } from '../../../core/service/language.service';
import { rentalObjectCommand } from '../../command/rental-object.command';

const formControlNames = {
  atticStorageNumber: 'atticStorageNumber',
  basementStorageNumber: 'basementStorageNumber',
  otherStorageNumber: 'otherStorageNumber',
  parkingIncludedInRent: 'parkingIncludedInRent',
  specialTerms: 'specialTerms',
  accessDate: 'accessDate',
  lastBillingDate: 'lastBillingDate',
  brfMortgagePrescriptions: 'brfMortgagePrescriptions',
};

@Component({
    selector: 'app-cooperative-housing-contract-form',
    templateUrl: './cooperative-housing-contract-form.component.html',
    styleUrls: ['./cooperative-housing-contract-form.component.scss'],
    providers: [
        ApproveContractCommandHandler,
        CooperativeHousingContractFormCommandHandlerResolver,
        DeleteFormCommandHandler,
        PrintContractTerminationFormCommandHandler,
        TerminateContractCommandHandler,
        VoidContractCommandHandler,
        ShowLeaseAgreementInvoicesCommandHandler,
        WithdrawApprovalCommandHandler,
        WithdrawTerminationCommandHandler,
        ESignLeaseAgreementContractCommandHandler,
        ESignKeyRecieptCommandHandler,
        PrintContractCommandHandler,
        TransferCooperativeHousingCommandHandler,
        { provide: ContractService, useExisting: CooperativeHousingContractService },
        { provide: DataService, useExisting: CooperativeHousingContractService },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CooperativeHousingContractFormComponent extends ContractFormComponentBase<CooperativeHousingContract> {
  constructor(
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    dataService: CooperativeHousingContractService,
    cdf: ColumnDefinitionFactory,
    cds: ColumnDefinitionService,
    fb: FormBuilder,
    fcf: FormControlExFactory,
    userAccount: UserAccountService,
    handlerResolver: CooperativeHousingContractFormCommandHandlerResolver,
    messageService: MessageService,
    messageDispatcher: ComponentMessageDispatcher,
    navigation: NavigationService,
    readonly contactFilterService: TenantService,
    readonly creditInstituteService: CreditInstituteService,
    logService: LeaseAgreementLogService,
    leaseAgreementImdConsumptionService: LeaseAgreementImdConsumptionService,
    languageService: LanguageService
  ) {
    super(
      messageDispatcher,
      cdf,
      fb,
      fcf,
      userAccount,
      cds,
      activatedRoute,
      dataService,
      messageService,
      navigation,
      commandDispatcher,
      handlerResolver,
      logService,
      leaseAgreementImdConsumptionService,
      languageService
    );

    this.menuDefinition = [];

    this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SEND_EMAIL', mailCommand.sendTenantEmail);
    this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SEND_LETTER', mailCommand.sendTenantMail);
    this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SEND_SMS', mailCommand.sendTenantSMS);

    this.AddSubscription(
      userAccount.current.subscribe({
        next: account => {
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.TRANSFER', leaseAgreementCommand.transferCooperativeHousing);
          }
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.APPROVE_TRANSFER', leaseAgreementCommand.approve);
          }
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.WITHDRAW_APPROVAL', leaseAgreementCommand.withdrawApproval);
          }
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.CREATE_INVOICE', leaseAgreementCommand.createInvoice);
          }
          this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SHOW_INVOICES', leaseAgreementCommand.showInvoices);
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.LINK_DOCUMENT', leaseAgreementCommand.linkDocument);
          }
          this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.BROKER_INFORMATION', rentalObjectCommand.printBrokerInformation);
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.VOID', leaseAgreementCommand.void);
          }
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addMenuItemDefinition('DELETE_BTN', standardCommand.delete);
          }
        },
      })
    );
  }

  category: LeaseAgreementCategory = LeaseAgreementCategory.CooperativeHousing;
  objectCategory: RentalObjectCategory = RentalObjectCategory.Dwelling;
  subleaseRequests: SubleaseRequest[];
  brfTransferForm: FormGroup; // TODO: rename to english
  showBrfTransfer: boolean; // TODO: rename to english
  owners: FormArray;
  cooperativeHousingDocumentCount = 0;

  linkDocument() {
    const cmd = new Command<CooperativeHousingContract>(leaseAgreementCommand.linkDocument);
    cmd.context = this.dataContext;
    this.commandDispatcher.sendCommand(cmd);
  }

  protected init(data: CooperativeHousingContract) {
    super.init(data);
    this.objectCategory = data.rentalObjects[0].category;
    this.subleaseRequests = data.subleaseRequests;
    if (this.isNew) {
      this.addOwner();
    }
  }

  isBrf(): boolean {
    return true;
  }

  protected getFormGroup(): FormGroup {
    const numberLength = 30;
    let group: FormGroup = super.getFormGroup();

    this._displayLastBillingDate = this.dataContext.lastBillingDate?.hasValue() || false;
    this.showBrfTransfer = true; // this.dataContext.brfTransfer ? true : false;
    this.fromDate = this.isNew
      ? LocalDate.fromDate(LocalDate.fromDate(LocalDate.today().firstDayInMonth()).addMonths(1))
      : this.dataContext.fromDate;

    this.brfTransferForm = this.fb.group({
      totalShare: this.fcf.create(
        'COOPERATIVE_HOUSING.TRANSFERRED_SHARE',
        this.dataContext.brfTransfer ? this.dataContext.brfTransfer.totalShare : null
      ),
      amount: this.fcf.create(
        'COOPERATIVE_HOUSING.TRANSFER_AMOUNT',
        this.dataContext.brfTransfer ? this.dataContext.brfTransfer.amount : new Money(this.dataContext.currencyCode, null)
      ),
      additionalPayment_639: this.fcf.create(
        'COOPERATIVE_HOUSING.KU55.ADDITIONAL_TRANSFER_AMOUNT_639',
        this.dataContext.brfTransfer ? this.dataContext.brfTransfer.additionalPayment_639 : new Money(this.dataContext.currencyCode, null)
      ),
      innerFund_635: this.fcf.create(
        'COOPERATIVE_HOUSING.KU55.INNER_FUND_635',
        this.dataContext.brfTransfer ? this.dataContext.brfTransfer.innerFund_635 : new Money(this.dataContext.currencyCode, null)
      ),
    });

    group.addControl(formControlNames.accessDate, this.fcf.createMandatory('COOPERATIVE_HOUSING.ACCESS_DATE', this.fromDate));
    group.addControl(
      formControlNames.lastBillingDate,
      this.fcf.create('COOPERATIVE_HOUSING.LAST_DEBIT_DATE', this.dataContext.lastBillingDate)
    );
    group.addControl(
      formControlNames.atticStorageNumber,
      this.fcf.createMaxLength(
        'DWELLING_LEASE_AGREEMENT_FORM.ATTIC_STORAGE_NUMBER_LABEL',
        this.dataContext.atticStorageNumber,
        numberLength
      )
    );
    group.addControl(
      formControlNames.basementStorageNumber,
      this.fcf.createMaxLength(
        'DWELLING_LEASE_AGREEMENT_FORM.BASEMENT_STORAGE_NUMBER_LABEL',
        this.dataContext.basementStorageNumber,
        numberLength
      )
    );
    group.addControl(
      formControlNames.otherStorageNumber,
      this.fcf.createMaxLength(
        'DWELLING_LEASE_AGREEMENT_FORM.OTHER_STORAGE_NUMBER_LABEL',
        this.dataContext.otherStorageNumber,
        numberLength
      )
    );
    group.addControl(
      formControlNames.parkingIncludedInRent,
      this.fcf.createMaxLength(
        'DWELLING_LEASE_AGREEMENT_FORM.PARKING_INCLUDED_IN_RENT_LABEL_BRF',
        this.dataContext.parkingIncludedInRent,
        numberLength,
        'LEASE_AGREEMENT.HELP_TEXT.PARKINGINCLUDED_BRF'
      )
    );
    group.addControl(
      formControlNames.brfMortgagePrescriptions,
      this.fb.array(this.dataContext.brfMortgagePrescriptions.map(item => this._createBrfMortgagePrescriptionGroup(item)))
    ); // TODO: rename to english
    group.addControl('subleaseRequests', this.fb.group({}));
    // Must be set before createOwnerForms is called...
    this.form = group;
    group.addControl('owners', this.createOwnerForms());
    this._setupAccessDateToMaximumMemberFromDates(group);

    return group;
  }

  protected getRentalObjectCaption(): string {
    return 'RENTAL_OBJECT.OBJECT';
  }

  private _setupAccessDateToMaximumMemberFromDates(group: FormGroup) {
    this.AddSubscription(
      group.controls[formControlNames.accessDate].valueChanges.subscribe(val => {
        if (!this.membershipDateUpdating) {
          for (let ownerForm of this.owners.controls.map(c => c as FormGroup)) {
            if (LocalDate.isLocalDate(val)) {
              const memberFromControl = ownerForm.controls['memberFrom'];
              if (
                memberFromControl &&
                (!memberFromControl.value ||
                  (LocalDate.isLocalDate(memberFromControl.value) && val.toDate().getTime() < memberFromControl.value.toDate().getTime()))
              ) {
                memberFromControl.setValue(val);
              }
            }
          }
        }
        this.fromDate = val;
      })
    );
  }

  get activeSubleaseRequestsCount(): number {
    if (!this.subleaseRequests || this.subleaseRequests.length === 0) {
      return 0;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return this.subleaseRequests.filter(request => {
      if (!request.fromDate || !request.toDate) {
        return false;
      }
      const fromDate = new Date(request.fromDate.toString());
      const toDate = new Date(request.toDate.toString());
      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(23, 59, 59, 999);
      return (fromDate <= today && today <= toDate) || fromDate > today;
    }).length;
  }

  get hasSecurity(): boolean {
    const hasDeposit =
      this.dataContext?.depositAmount?.amount > 0 ||
      (this.dataContext?.depositComment && this.dataContext.depositComment.trim().length > 0);
    const hasGuarantor = this.dataContext?.securities && this.dataContext.securities.length > 0;
    return hasDeposit || hasGuarantor;
  }

  private membershipDateUpdating: boolean = false;
  private _setupMemberFromToMinimumAccessDate(form: FormGroup) {
    this.AddSubscription(
      form.controls['memberFrom'].valueChanges.subscribe(val => {
        // Set the earliestMemberFromDate on the access date if the access date is before the earliest member from date.
        // Because you most be a member of the cooperative-housing to get access to the apartment.
        const earliestMemberFromDate = this.getEarliestMemberFromDate();
        if (earliestMemberFromDate) {
          this.membershipDateUpdating = true;
          const accessDateControl = this.form.controls[formControlNames.accessDate];
          if (
            accessDateControl &&
            (!accessDateControl.value ||
              (LocalDate.isLocalDate(accessDateControl.value) &&
                LocalDate.isLocalDate(earliestMemberFromDate) &&
                accessDateControl.value.toDate().getTime() < earliestMemberFromDate.toDate().getTime()))
          ) {
            accessDateControl.setValue(earliestMemberFromDate);
          }
          this.membershipDateUpdating = false;
        }
      })
    );
  }

  getEarliestMemberFromDate(): LocalDate {
    const ownerForms = this.owners.controls.map(c => c as FormGroup);
    const earliestMemberFromDate = ownerForms
      .map(ownerForm => ownerForm.controls['memberFrom']?.value)
      .filter(value => !!value)
      .reduce((latestDate, memberFromValue) => {
        if (!latestDate || memberFromValue.toDate() <= latestDate.toDate()) {
          return memberFromValue;
        }
        return latestDate;
      }, null);

    return earliestMemberFromDate;
  }

  get displayLastBillingDate(): boolean {
    return this._displayLastBillingDate;
  }
  _displayLastBillingDate: boolean = false;

  onRentalObjectChanged(rentalObject: VacantRentalObject) {
    super.onRentalObjectChanged(rentalObject);
    if (rentalObject?.rentalObjectId) {
      this.dataContext.rentalObjectId = rentalObject.rentalObjectId;
      this.dataContext.atticStorageNumber = rentalObject.atticStorageNumber;
      this.form.controls[formControlNames.atticStorageNumber].setValue(rentalObject.atticStorageNumber);
      this.dataContext.basementStorageNumber = rentalObject.basementStorageNumber;
      this.form.controls[formControlNames.basementStorageNumber].setValue(rentalObject.basementStorageNumber);
      this.dataContext.otherStorageNumber = rentalObject.otherStorageNumber;
      this.form.controls[formControlNames.otherStorageNumber].setValue(rentalObject.otherStorageNumber);
    }
  }

  onTenantSelected(item: AutoCompleteSelectionItem, ownerForm: FormGroup) {
    const tenant = item?.data || null;
    ownerForm.controls['name'].setValue(tenant?.name);
    ownerForm.controls['email'].setValue(tenant?.email);
    ownerForm.controls['phone'].setValue(tenant?.phone);
    if ((this.owners.controls[0] as FormGroup) === ownerForm) {
      this.tenantOne = tenant;
      ownerForm.controls['contactId'].setValue(tenant?.id);
      this.form.controls['tenantOne'].setValue(tenant);
    }
  }

  private _validateShare(control: AbstractControl): ValidationErrors {
    let totalShare = this.owners.controls
      .map(c => numberValue((c as FormGroup).controls['share'].value))
      .filter(c => c)
      .reduce((partialSum, a) => partialSum + a, 0);
    if (totalShare !== 100) {
      return { function: { message: 'COOPERATIVE_HOUSING.VALIDATION.TOTAL_SHARE_MUST_BE_100' } };
    }
    return null;
  }

  createOwnerForms(): FormArray {
    this.owners = this.fb.array([]);
    for (let owner of this.dataContext.owners) {
      this.owners.push(this.createOwnerForm(owner));
    }
    return this.owners;
  }

  addOwner() {
    this.owners.push(this.createOwnerForm(null));
  }

  onOpenOwnerClicked(id: string): void {
    {
      this.navigation.navigate(['tenants', 'edit', id]);
    }
  }

  deleteOwner(index: number) {
    this.owners.removeAt(index);
    this._updateShareValidity();
  }

  private _updateShareValidity() {
    for (let form of this.owners.controls.map(c => c as FormGroup)) {
      form.controls['share'].markAsTouched();
      form.controls['share'].updateValueAndValidity({ emitEvent: false });
    }
  }

  createOwnerForm(owner: CooperativeHousingOwner): FormGroup {
    let requireApproval = !this.isNew && this.dataContext.status !== LeaseAgreementStatus.Draft;
    var form = this.fb.group({
      id: this.fcf.createHidden(owner ? owner.id : null),
      contactId: this.fcf.createMandatory('COOPERATIVE_HOUSING.OWNER', owner ? owner.contactId : null),
      share: this.fcf.createWithOptions('COOPERATIVE_HOUSING.OWNED_SHARE', owner ? owner.share : null, {
        required: true,
        maxValue: 100,
        minValue: 0.01,
        validator: control => this._validateShare(control),
      }),
      name: this.fcf.createReadonly('CONTACT.NAME', owner ? owner.name : null),
      email: this.fcf.createReadonly('COMMON.EMAIL', owner ? owner.email : null),
      phone: this.fcf.createReadonly('COMMON.PHONE_NUMBER', owner ? owner.phone : null),
      memberFrom: this.fcf.createWithOptions(
        'COOPERATIVE_HOUSING.MEMBER_FROM',
        owner ? LocalDate.fromDate(owner.memberFrom) : this.fromDate,
        { required: requireApproval }
      ),
      memberTo: this.fcf.create(
        'COOPERATIVE_HOUSING.MEMBER_TO',
        owner?.memberTo ? LocalDate.fromDate(owner.memberTo) : null
      ),
      amortizationAmount: this.fcf.createReadonly('COOPERATIVE_HOUSING.AMORTIZATION_AMOUNT', owner ? owner.amortizationAmount : null),
      approvalDate: this.fcf.createWithOptions('COOPERATIVE_HOUSING.MEMBER_APPROVAL_DATE', owner ? owner.approvalDate : LocalDate.today(), {
        required: requireApproval,
      }),
    });
    this.AddSubscription(form.controls['share'].valueChanges.subscribe(val => this._updateShareValidity()));
    this._setupMemberFromToMinimumAccessDate(form);

    return form;
  }

  private _createBrfMortgagePrescriptionGroup(prescription: BrfMortgagePrescription): FormGroup {
    var mortgageForm = this.fb.group({
      id: this.fcf.createHidden(prescription ? prescription.id : null),
      rentalObjectId: this.fcf.createHidden(prescription ? prescription.rentalObjectId : null),
      leaseAgreementId: this.fcf.createHidden(prescription ? prescription.leaseAgreementId : null),
      creditInstituteId: this.fcf.createMandatory('MORTGAGE.CREDIT_INSTITUTE', prescription ? prescription.creditInstituteId : null),
      mortgageOrder: this.fcf.createMandatory('COOPERATIVE_HOUSING.MORTGAGE_ORDER', prescription ? prescription.mortgageOrder : null),
      mortgageNumber: this.fcf.create(
        'COOPERATIVE_HOUSING.MORTGAGE_PRESCRIPTION_NUMBER',
        prescription ? prescription.mortgageNumber : null
      ),
      fromDate: this.fcf.createMandatory('COOPERATIVE_HOUSING.MORTGAGE_FROM', prescription ? prescription.fromDate : null),
      toDate: this.fcf.create('COOPERATIVE_HOUSING.MORTGAGE_TO', prescription ? prescription.toDate : null),
      amount: this.fcf.create('COOPERATIVE_HOUSING.MORTGAGE_AMOUNT', prescription ? prescription.amount : null),
    });

    return mortgageForm;
  }

  override setObjectCategory(category: RentalObjectCategory) {
    this.objectCategory = category;
  }

  override getLeaseAgreementCategory(): LeaseAgreementCategory {
    switch (this.objectCategory) {
      case RentalObjectCategory.Dwelling:
        return LeaseAgreementCategory.Dwelling;
      case RentalObjectCategory.Parking:
        return LeaseAgreementCategory.Parking;
      case RentalObjectCategory.Premises:
        return LeaseAgreementCategory.Premises;
      case RentalObjectCategory.Other:
        return LeaseAgreementCategory.Other;
      default:
        return super.getLeaseAgreementCategory();
    }
  }

  get brfMortgagePrescriptions(): FormArray {
    return this.form.controls[formControlNames.brfMortgagePrescriptions] as FormArray;
  }

  addBrfMortgagePrescription() {
    this.brfMortgagePrescriptions.push(this._createBrfMortgagePrescriptionGroup(new BrfMortgagePrescription()));
  }

  removeBrfMortgagePrescription(index: number) {
    this.brfMortgagePrescriptions.removeAt(index);
    this.form.markAsDirty();
  }

  protected createModel(): CooperativeHousingContract {
    let model = new CooperativeHousingContract();
    model.invoiceAddress.country = '';
    return model;
  }

  protected prepareSave(): CooperativeHousingContract {
    const model = super.prepareSave();
    model.subleaseRequests = this.subleaseRequests;
    // Convert the form values to their model type explicitly. This makes sure
    // LocalDate-backed fields, including the optional delisted date (toDate),
    // use BrfMortgagePrescription.toJSON() when the contract is saved.
    model.brfMortgagePrescriptions = this.brfMortgagePrescriptions.controls.map(
      control => new BrfMortgagePrescription(control.value)
    );
    model.fromDate = this.form.controls[formControlNames.accessDate].value;
    // Cooperative-housing agreements have no notice-period concept in the UI, and
    // CooperativeHousingAgreementMappingExtensions never reads or writes NoticePeriod
    // server-side -- accessDate/fromDate is the only field that is actually persisted.
    // The unused field is dropped from the wire format by
    // CooperativeHousingContract.toJSON() rather than cleared on the live model here,
    // because Contract's toDate/toJSON still dereference noticePeriod.
    model.rentalObjectId = this.dataContext.rentalObjectId;

    return model;
  }
}
